import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { Plus, Trash2, Pencil, Eye, Printer, Search, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useGlobalBarcodeScanner } from '@/hooks/useGlobalBarcodeScanner';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DigitalDesignData() {
  const navigate = useNavigate();
  const [designs, setDesigns] = useState([]);
  const [filteredDesigns, setFilteredDesigns] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [uniqueCategories, setUniqueCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editDesign, setEditDesign] = useState(null);
  const [viewDetails, setViewDetails] = useState(null);
  const [printBarcode, setPrintBarcode] = useState(null);
  const [printSize, setPrintSize] = useState({ width: 50, height: 30 });
  const [colorSearchQuery, setColorSearchQuery] = useState('');
  const [formData, setFormData] = useState({ 
    ln_t_code: 'LN-T-', 
    digital_design_category: '',
    design_type: '', // PRINT or EMBROIDERY
    price_per_meter: '', // Optional price field for PRINT and EMBROIDERY
    colors: [], // For PRINT type
    embroidery_image: '' // For EMBROIDERY type
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [designToDelete, setDesignToDelete] = useState(null);
  const [colorToDelete, setColorToDelete] = useState(null);
  const [deleteColorDialogOpen, setDeleteColorDialogOpen] = useState(false);
  const [imagePopup, setImagePopup] = useState(null); // For full-size image view

  // Global barcode scanner for Digital Designs
  const handleGlobalBarcodeScanned = (scannedCode) => {
    const found = designs.find(design => design.ln_t_code.toLowerCase() === scannedCode.toLowerCase());
    if (found) {
      setViewDetails(found);
      toast.success(`✅ Digital Design found: ${found.ln_t_code}`);
    } else {
      toast.error(`❌ No digital design found with barcode: ${scannedCode}`);
    }
  };

  useGlobalBarcodeScanner(handleGlobalBarcodeScanned);

  useEffect(() => { fetchDesigns(); }, []);

  // Extract unique categories whenever designs data changes
  useEffect(() => {
    const categories = [...new Set(designs.map(d => d.digital_design_category).filter(Boolean))];
    setUniqueCategories(categories.sort());
  }, [designs]);

  useEffect(() => {
    let filtered = designs;

    // Filter by LN-T Code (exact and partial matching for barcode scanning)
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(design => {
        // Exact match first (for barcode scanning)
        if (design.ln_t_code.toLowerCase() === query) {
          return true;
        }
        // Partial match (for regular typing)
        return design.ln_t_code.toLowerCase().includes(query);
      });
    }

    // Filter by Digital Design Category
    if (categoryFilter !== '') {
      filtered = filtered.filter(design => 
        design.digital_design_category === categoryFilter
      );
    }

    setFilteredDesigns(filtered);
  }, [searchQuery, categoryFilter, designs]);

  const extractNumeric = (code) => {
    return code.replace(/\D/g, '').replace(/^0+/, '');
  };

  const handleBarcodeSearch = (e) => {
    if (e && e.key === 'Enter' && searchQuery.trim() !== '') {
      e.preventDefault();
      const query = searchQuery.toLowerCase().trim();
      
      console.log('🔍 Searching digital designs for:', query);
      
      // Find digital design by exact or partial match
      const found = designs.find(design => {
        return design.ln_t_code.toLowerCase() === query || 
               design.ln_t_code.toLowerCase().includes(query);
      });
      
      if (found) {
        console.log('✅ Found digital design:', found.ln_t_code);
        setViewDetails(found);
        toast.success(`✅ Found: ${found.ln_t_code}`);
      } else {
        console.log('❌ No digital design found for:', query);
        toast.error(`❌ No design found: ${query}`);
      }
    }
  };

  const fetchDesigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/digital-designs-master`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDesigns(response.data);
      setFilteredDesigns(response.data);
    } catch (error) {
      toast.error('Failed to fetch digital designs');
    }
  };

  // Auto-refresh every 5 seconds, but pause when user is editing
  const isUserEditing = open || !!editDesign;
  useAutoRefresh(fetchDesigns, 5, isUserEditing);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.ln_t_code || !formData.digital_design_category || !formData.design_type) {
      setErrorMessage('Please fill in all required fields');
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (formData.design_type === 'PRINT' && formData.colors.length === 0) {
      setErrorMessage('Please add at least one color for PRINT design type');
      toast.error('Please add at least one color for PRINT design type');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Prepare data - ensure colors array is properly structured
      const submitData = {
        ...formData,
        price_per_meter: formData.price_per_meter ? parseFloat(formData.price_per_meter) : 0
      };
      
      console.log('Submitting digital design data:', submitData);
      
      if (editDesign) {
        await axios.put(`${API}/digital-designs-master/${editDesign.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Digital Design updated successfully');
      } else {
        await axios.post(`${API}/digital-designs-master`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Digital Design created successfully');
      }
      
      // Close dialog and reset form
      setOpen(false);
      setEditDesign(null);
      setErrorMessage('');
      setFormData({ 
        ln_t_code: 'LN-T-', 
        digital_design_category: '',
        design_type: '',
        price_per_meter: '',
        colors: [],
        embroidery_image: ''
      });
      
      // Refresh designs list
      await fetchDesigns();
    } catch (error) {
      console.error('Digital Design save error:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMsg = error.response?.data?.detail || 
                      error.response?.data?.message || 
                      error.message || 
                      'Failed to save digital design. Please try again.';
      
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (design) => {
    setDesignToDelete(design);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!designToDelete) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/digital-designs-master/${designToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted successfully');
      setDeleteDialogOpen(false);
      setDesignToDelete(null);
      fetchDesigns();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setDesignToDelete(null);
  };

  // Color management for PRINT type
  const handleAddColor = () => {
    setFormData({
      ...formData,
      colors: [...formData.colors, { color_name: '', color_code: '', image: '' }]
    });
  };

  const handleColorChange = (index, field, value) => {
    const updatedColors = [...formData.colors];
    updatedColors[index][field] = value;
    setFormData({ ...formData, colors: updatedColors });
  };

  const handleColorImageUpload = async (index, file) => {
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const updatedColors = [...formData.colors];
      updatedColors[index].image = reader.result;
      setFormData({ ...formData, colors: updatedColors });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveColor = (index) => {
    setColorToDelete(index);
    setDeleteColorDialogOpen(true);
  };

  const confirmColorDelete = async () => {
    if (colorToDelete === null) return;
    const updatedColors = formData.colors.filter((_, i) => i !== colorToDelete);
    setFormData({ ...formData, colors: updatedColors });
    setDeleteColorDialogOpen(false);
    setColorToDelete(null);
    toast.success('Color removed');
  };

  const cancelColorDelete = () => {
    setDeleteColorDialogOpen(false);
    setColorToDelete(null);
  };

  // Embroidery image upload
  const handleEmbroideryImageUpload = async (file) => {
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({ ...formData, embroidery_image: reader.result });
    };
    reader.readAsDataURL(file);
  };

    const handlePrint = () => {
    if (!printBarcode) return;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Barcode - ${printBarcode.code}</title>
          <style>
            @page { 
              size: ${printSize.width}mm ${printSize.height}mm;
              margin: 0;
            }
            @media print {
              body { margin: 0; padding: 0; }
            }
            body { 
              margin: 0; 
              padding: 0; 
              display: flex; 
              justify-content: center; 
              align-items: center; 
              width: ${printSize.width}mm;
              height: ${printSize.height}mm;
            }
            .barcode-container { 
              text-align: center;
              page-break-inside: avoid;
              width: 100%;
              height: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            img { 
              max-width: 100%;
              max-height: 100%;
              display: block;
            }
          </style>
        </head>
        <body>
          <div class="barcode-container">
            <img src="${printBarcode.image}" alt="Barcode" onload="window.print(); window.onafterprint = function() { window.close(); }" />
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Digital Design (LN-T-CODE)</h1>
              <p className="text-sm text-gray-600">Manage digital designs</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6 space-y-4">
        {/* Main Search Bar */}
        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search or scan barcode" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} 
              onKeyDown={handleBarcodeSearch} 
              className="pl-10"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              tabIndex="1"
              data-barcode-input="true"
            />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={async () => { 
                setEditDesign(null); 
                try {
                  const token = localStorage.getItem('token');
                  const response = await fetch(`${API}/master-data/next-code/digital_design`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  if (response.ok) {
                    const data = await response.json();
                    setFormData({ 
                      ln_t_code: data.next_code, 
                      digital_design_category: '',
                      design_type: '',
                      price_per_meter: '',
                      colors: [],
                      embroidery_image: ''
                    });
                  } else {
                    setFormData({ 
                      ln_t_code: 'LN-T-00001', 
                      digital_design_category: '',
                      design_type: '',
                      price_per_meter: '',
                      colors: [],
                      embroidery_image: ''
                    });
                  }
                } catch (error) {
                  console.error('Error fetching next code:', error);
                  setFormData({ 
                    ln_t_code: 'LN-T-00001', 
                    digital_design_category: '',
                    design_type: '',
                    price_per_meter: '',
                    colors: [],
                    embroidery_image: ''
                  });
                }
              }}>
                <Plus className="w-4 h-4 mr-2" /> Add Digital Design
              </Button>
            </DialogTrigger>
          </Dialog>
        </div>

        {/* Filters Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Digital Design Category</Label>
            <SearchableSelect
              options={[
                { value: "", label: "All Categories" },
                ...uniqueCategories.map(category => ({ value: category, label: category }))
              ]}
              value={categoryFilter}
              onChange={(value) => setCategoryFilter(value)}
              placeholder="All Categories"
              searchPlaceholder="Search category..."
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium">Actions</Label>
            <Button 
              variant="outline" 
              onClick={() => {
                setSearchQuery('');
                setCategoryFilter('');
              }}
              className="w-full text-sm"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) setErrorMessage(''); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editDesign ? 'Edit Digital Design' : 'Add New Digital Design (LN-T-CODE)'}</DialogTitle></DialogHeader>
            {errorMessage && (
              <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-bold text-red-800 mb-1">Error</h3>
                    <p className="text-sm text-red-700">{errorMessage}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setErrorMessage('')}
                    className="ml-auto flex-shrink-0 inline-flex text-red-400 hover:text-red-600"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label><span className="text-red-500">*</span> LN-T Code</Label>
                <Input 
                  value={formData.ln_t_code} 
                  onChange={(e) => {
                    setFormData({ ...formData, ln_t_code: e.target.value });
                  }}
                  required 
                  placeholder="LN-T-00001" 
                />
                <p className="text-xs text-gray-500">Auto-generated, but you can edit it</p>
              </div>
              <div className="space-y-2">
                <Label><span className="text-red-500">*</span> Digital Design Category</Label>
                <Input value={formData.digital_design_category} onChange={(e) => setFormData({ ...formData, digital_design_category: e.target.value })} 
                  required placeholder="Enter digital design category" />
              </div>

              <div className="space-y-2">
                <Label><span className="text-red-500">*</span> Design Type</Label>
                <SearchableSelect
                  options={[
                    { value: "", label: "Select Design Type" },
                    { value: "PRINT", label: "PRINT" },
                    { value: "EMBROIDERY", label: "EMBROIDERY" }
                  ]}
                  value={formData.design_type}
                  onChange={(value) => setFormData({ ...formData, design_type: value, colors: [], embroidery_image: '' })}
                  placeholder="Select Type"
                  searchPlaceholder="Search type..."
                  required
                />
              </div>

              {/* Price Per Meter - Optional field for PRINT and EMBROIDERY */}
              {(formData.design_type === 'PRINT' || formData.design_type === 'EMBROIDERY') && (
                <div className="space-y-2">
                  <Label>Price Per Meter</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price_per_meter}
                    onChange={(e) => setFormData({ ...formData, price_per_meter: e.target.value })}
                    placeholder="$"
                  />
                </div>
              )}

              {/* PRINT - Color Management */}
              {formData.design_type === 'PRINT' && (
                <div className="space-y-4 border-2 border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <Label className="text-base font-semibold">Colors</Label>
                    <Button type="button" size="sm" onClick={handleAddColor}>
                      <Plus className="w-4 h-4 mr-1" /> Add Color
                    </Button>
                  </div>
                  
                  {formData.colors.map((color, index) => (
                    <div key={index} className="border border-gray-300 rounded-lg p-3 space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Color {index + 1}</span>
                        <Button type="button" size="sm" variant="ghost" onClick={() => handleRemoveColor(index)} className="text-red-600 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Color Name</Label>
                          <Input
                            value={color.color_name}
                            onChange={(e) => handleColorChange(index, 'color_name', e.target.value)}
                            placeholder="Enter color name"
                            className="text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Color Code</Label>
                          <Input
                            value={color.color_code}
                            onChange={(e) => handleColorChange(index, 'color_code', e.target.value)}
                            placeholder="Enter color code"
                            className="text-sm"
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs">Color Image</Label>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleColorImageUpload(index, e.target.files[0])}
                          className="text-sm border-0 p-0 overflow-visible file:mr-4 file:py-1.5 file:px-3 file:rounded file:border-2 file:border-black file:text-xs file:font-semibold file:bg-white file:text-black hover:file:bg-gray-100 file:cursor-pointer"
                        />
                        {(color.image_path || color.image) && (
                          <div className="mt-2">
                            <img 
                              src={color.image_path ? `${API}/${color.image_path}` : color.image} 
                              alt={`Color ${index + 1}`} 
                              className="w-20 h-20 object-cover rounded border" 
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* EMBROIDERY - Single Image Upload */}
              {formData.design_type === 'EMBROIDERY' && (
                <div className="space-y-2 border-2 border-gray-200 rounded-lg p-4">
                  <Label>Embroidery Image</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleEmbroideryImageUpload(e.target.files[0])}
                    className="text-sm border-0 p-0 overflow-visible file:mr-4 file:py-1.5 file:px-3 file:rounded file:border-2 file:border-black file:text-xs file:font-semibold file:bg-white file:text-black hover:file:bg-gray-100 file:cursor-pointer"
                  />
                  {formData.embroidery_image && (
                    <div className="mt-2">
                      <img src={formData.embroidery_image} alt="Embroidery" className="w-32 h-32 object-cover rounded border" />
                    </div>
                  )}
                </div>
              )}

              <Button type="submit" className="w-full">{editDesign ? 'Update' : 'Create'} Digital Design</Button>
            </form>
          </DialogContent>
        </Dialog>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {filteredDesigns.map((design) => (
          <div key={design.id} className="bg-white border-2 border-black rounded-lg shadow p-4 flex flex-col items-center justify-between hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => setViewDetails(design)}>
            <h3 className="text-lg font-bold text-center mb-2">{design.ln_t_code}</h3>
            <p className="text-xs text-gray-500 text-center mb-3">{design.digital_design_category}</p>
            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" onClick={() => { setEditDesign(design); setFormData(design); setOpen(true); }}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => handleDelete(design)}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {filteredDesigns.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No digital designs found.</p>
        </div>
      )}

      <Dialog open={!!viewDetails} onOpenChange={() => { setViewDetails(null); setColorSearchQuery(''); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Digital Design Details - {viewDetails?.ln_t_code}</DialogTitle></DialogHeader>
          {viewDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="font-semibold">LN-T Code:</span> {viewDetails.ln_t_code}</div>
                <div><span className="font-semibold">Category:</span> {viewDetails.digital_design_category}</div>
                <div><span className="font-semibold">Design Type:</span> {viewDetails.design_type}</div>
                {viewDetails.price_per_meter && (
                  <div><span className="font-semibold">Price Per Meter:</span> ${Number(viewDetails.price_per_meter).toFixed(2)}/Meter</div>
                )}
              </div>
              
              {/* Main Barcode */}
              {viewDetails.barcode_path && (
                <div className="border rounded p-4 bg-gray-50">
                  <div className="space-y-2">
                    <Button variant="outline" size="sm" onClick={() => setPrintBarcode({ 
                      image: `${API}/${viewDetails.barcode_path}`, 
                      code: viewDetails.ln_t_code 
                    })}>
                      <Printer className="w-3 h-3 mr-1" /> Print Main Barcode
                    </Button>
                  </div>
                </div>
              )}

              {/* PRINT Type - Show Colors with Barcodes */}
              {viewDetails.design_type === 'PRINT' && viewDetails.colors && viewDetails.colors.length > 0 && (
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold">Colors ({viewDetails.colors?.filter(color => {
                      if (!colorSearchQuery.trim()) return true;
                      
                      let query = colorSearchQuery.trim();
                      
                      // If it's a full barcode (e.g., "LN-T-00001-001"), extract just the color part
                      if (query.includes('-') && query.split('-').length >= 3) {
                        query = query.split('-').pop(); // Get the last part (color code)
                      }
                      
                      // Clean the query and color code for comparison
                      query = query.replace(/\D/g, '').replace(/^0+/, '') || query;
                      const colorCode = color.color_code.replace(/\D/g, '').replace(/^0+/, '') || color.color_code;
                      
                      return colorCode.includes(query) || color.color_code.toLowerCase().includes(colorSearchQuery.toLowerCase());
                    }).length || 0})</h4>
                    <div className="relative flex-1 max-w-xs ml-4">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                      <Input 
                        placeholder="Search or scan barcode" 
                        value={colorSearchQuery}
                        onChange={(e) => setColorSearchQuery(e.target.value)}
                        className="pl-10"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        tabIndex="2"
                        data-barcode-input="true"
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    {viewDetails.colors?.filter(color => {
                      if (!colorSearchQuery.trim()) return true;
                      
                      let query = colorSearchQuery.trim();
                      
                      // If it's a full barcode (e.g., "LN-T-00001-001"), extract just the color part
                      if (query.includes('-') && query.split('-').length >= 3) {
                        query = query.split('-').pop(); // Get the last part (color code)
                      }
                      
                      // Clean the query and color code for comparison
                      query = query.replace(/\D/g, '').replace(/^0+/, '') || query;
                      const colorCode = color.color_code.replace(/\D/g, '').replace(/^0+/, '') || color.color_code;
                      
                      return colorCode.includes(query) || color.color_code.toLowerCase().includes(colorSearchQuery.toLowerCase());
                    }).map((color, idx) => (
                      <div key={idx} className="border rounded p-4 bg-gray-50">
                        <div className="mb-3">
                          <div className="mb-2">
                            <span className="font-medium">Code:</span> {color.barcode?.code || `${viewDetails.ln_t_code}-${color.color_code}`}
                          </div>
                          <div className="mb-2">
                            <span className="font-medium">Name:</span> {color.color_name}
                          </div>
                        </div>
                        {(color.image_path || color.image) && (
                          <div className="mb-3">
                            <span className="font-medium block mb-2">Image:</span>
                            <div className="flex justify-center">
                              <img 
                                src={color.image_path ? `${API}/${color.image_path}` : color.image} 
                                alt={color.color_name} 
                                className="max-w-xs max-h-64 object-contain rounded border cursor-pointer hover:opacity-80 transition-opacity" 
                                onClick={() => setImagePopup({ image: color.image_path ? `${API}/${color.image_path}` : color.image, name: color.color_name })}
                                title="Click to enlarge"
                              />
                            </div>
                          </div>
                        )}
                        {color.barcode_path && (
                          <div className="space-y-2 mt-3">
                            <Button variant="outline" size="sm" className="w-full" onClick={() => setPrintBarcode({ 
                              image: `${API}/${color.barcode_path}`, 
                              code: `${viewDetails.ln_t_code}-${color.color_code}` 
                            })}>
                              <Printer className="w-3 h-3 mr-1" /> Print Color Barcode
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* EMBROIDERY Type - Show Single Image */}
              {viewDetails.design_type === 'EMBROIDERY' && (viewDetails.embroidery_image_path || viewDetails.embroidery_image) && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Embroidery Image</h4>
                  <img 
                    src={viewDetails.embroidery_image_path ? `${API}/${viewDetails.embroidery_image_path}` : viewDetails.embroidery_image} 
                    alt="Embroidery" 
                    className="w-48 h-48 object-cover rounded border mx-auto cursor-pointer hover:opacity-80 transition-opacity" 
                    onClick={() => setImagePopup({ image: viewDetails.embroidery_image_path ? `${API}/${viewDetails.embroidery_image_path}` : viewDetails.embroidery_image, name: 'Embroidery Design' })}
                    title="Click to enlarge"
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!printBarcode} onOpenChange={() => setPrintBarcode(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Print Barcode - {printBarcode?.code}</DialogTitle></DialogHeader>
          {printBarcode && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <img src={printBarcode.image} alt="Barcode" className="max-w-full" />
              </div>
              <div className="space-y-3">
                <Label>Print Size (mm)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Width</Label>
                    <Input type="number" value={printSize.width} onChange={(e) => setPrintSize({ ...printSize, width: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-sm">Height</Label>
                    <Input type="number" value={printSize.height} onChange={(e) => setPrintSize({ ...printSize, height: e.target.value })} />
                  </div>
                </div>
              </div>
              <Button onClick={handlePrint} className="w-full">
                <Printer className="w-4 h-4 mr-2" /> Print Barcode
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Full-Size Image Popup */}
      <Dialog open={!!imagePopup} onOpenChange={() => setImagePopup(null)}>
        <DialogContent className="max-w-6xl max-h-[90vh] p-0">
          <DialogHeader className="p-4 border-b">
            <DialogTitle>Image Preview - {imagePopup?.name}</DialogTitle>
          </DialogHeader>
          {imagePopup && (
            <div className="p-4 overflow-auto flex items-center justify-center" style={{ maxHeight: 'calc(90vh - 80px)' }}>
              <img 
                src={imagePopup.image} 
                alt={imagePopup.name} 
                className="max-w-full h-auto object-contain"
                style={{ cursor: 'zoom-in' }}
                onClick={(e) => {
                  if (e.target.style.transform === 'scale(2)') {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.cursor = 'zoom-in';
                  } else {
                    e.target.style.transform = 'scale(2)';
                    e.target.style.cursor = 'zoom-out';
                  }
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AuthorizationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onAuthorized={confirmDelete}
        title="Delete Digital Design"
        message={designToDelete ? `Enter authorization code to delete digital design ${designToDelete.ln_t_code}` : 'Enter authorization code to proceed'}
      />

      {/* Delete Color Authorization Dialog */}
      <AuthorizationDialog
        open={deleteColorDialogOpen}
        onOpenChange={setDeleteColorDialogOpen}
        onAuthorized={confirmColorDelete}
        title="Remove Color"
        message={colorToDelete !== null && formData.colors[colorToDelete] ? `Enter authorization code to remove color ${formData.colors[colorToDelete].color_name}` : 'Enter authorization code to proceed'}
      />
      </main>
    </div>
  );
}